
import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Basic client‑side validation
    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch(
        'https://readdy.ai/api/form/d63c43qbdoed7v0v8mn0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 px-8 bg-white relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Left Column */}
          <div>
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-8 h-px bg-[#00e5c7]"></div>
              <span className="text-[#00e5c7] text-xs uppercase tracking-[0.3em] font-semibold">
                Get In Touch
              </span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#0a1628] mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {/* Fixed Unicode escape – use a real apostrophe */}
              Let’s Discuss Your Tax Strategy
            </h2>
            <p className="text-sm text-[#5a6a7e] leading-relaxed mb-12">
              Schedule a consultation with our expert team to explore how we
              can help optimize your tax situation and achieve your financial
              goals.
            </p>

            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#0a1628] flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-lg text-[#00e5c7]"></i>
                </div>
                <div>
                  <div className="text-xs text-[#5a6a7e] mb-1 uppercase tracking-wider">
                    Phone
                  </div>
                  <a
                    href="tel:+17147669527"
                    className="text-base font-semibold text-[#0a1628] hover:text-[#00e5c7] transition-colors cursor-pointer"
                  >
                    (714) 766-9527
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#0a1628] flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-lg text-[#00e5c7]"></i>
                </div>
                <div>
                  <div className="text-xs text-[#5a6a7e] mb-1 uppercase tracking-wider">
                    Email
                  </div>
                  <a
                    href="mailto:monge.alejandro.tax@gmail.com"
                    className="text-base font-semibold text-[#0a1628] hover:text-[#00e5c7] transition-colors cursor-pointer"
                  >
                    monge.alejandro.tax@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#0a1628] flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-lg text-[#00e5c7]"></i>
                </div>
                <div>
                  <div className="text-xs text-[#5a6a7e] mb-1 uppercase tracking-wider">
                    Office
                  </div>
                  <a
                    href="https://maps.google.com/?q=Orange,CA+92869"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-base font-semibold text-[#0a1628] hover:text-[#00e5c7] transition-colors cursor-pointer"
                  >
                    Orange, CA 92869
                  </a>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <a
                href="#"
                rel="nofollow"
                className="w-10 h-10 rounded-lg bg-[#0a1628] flex items-center justify-center hover:bg-[#00e5c7] transition-all cursor-pointer group"
              >
                <i className="ri-linkedin-fill text-lg text-[#00e5c7] group-hover:text-[#0a1628]"></i>
              </a>
              <a
                href="#"
                rel="nofollow"
                className="w-10 h-10 rounded-lg bg-[#0a1628] flex items-center justify-center hover:bg-[#00e5c7] transition-all cursor-pointer group"
              >
                <i className="ri-twitter-fill text-lg text-[#00e5c7] group-hover:text-[#0a1628]"></i>
              </a>
              <a
                href="#"
                rel="nofollow"
                className="w-10 h-10 rounded-lg bg-[#0a1628] flex items-center justify-center hover:bg-[#00e5c7] transition-all cursor-pointer group"
              >
                <i className="ri-facebook-fill text-lg text-[#00e5c7] group-hover:text-[#0a1628]"></i>
              </a>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-[#f8f9fb] rounded-lg p-8 border border-gray-200/80">
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-readdy-form
              id="contact-form"
            >
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:border-[#00e5c7] focus:ring-1 focus:ring-[#00e5c7]/20 outline-none transition-all text-sm text-[#0a1628] placeholder-[#8a9bb5]"
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:border-[#00e5c7] focus:ring-1 focus:ring-[#00e5c7]/20 outline-none transition-all text-sm text-[#0a1628] placeholder-[#8a9bb5]"
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:border-[#00e5c7] focus:ring-1 focus:ring-[#00e5c7]/20 outline-none transition-all text-sm text-[#0a1628] placeholder-[#8a9bb5]"
                />
              </div>

              <div>
                <select
                  name="service"
                  value={formData.service}
                  onChange={(e) =>
                    setFormData({ ...formData, service: e.target.value })
                  }
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:border-[#00e5c7] focus:ring-1 focus:ring-[#00e5c7]/20 outline-none transition-all text-sm text-[#0a1628] cursor-pointer"
                >
                  <option value="">Select Service Interest</option>
                  <option value="Tax Strategy Planning">
                    Tax Strategy Planning
                  </option>
                  <option value="Business Tax Preparation">
                    Business Tax Preparation
                  </option>
                  <option value="Individual Tax Filing">
                    Individual Tax Filing
                  </option>
                  <option value="IRS Representation">
                    IRS Representation
                  </option>
                  <option value="Estate Planning">Estate Planning</option>
                  <option value="Quarterly Reviews">Quarterly Reviews</option>
                </select>
              </div>

              <div>
                <textarea
                  name="message"
                  placeholder="Tell us about your tax needs..."
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:border-[#00e5c7] focus:ring-1 focus:ring-[#00e5c7]/20 outline-none transition-all text-sm text-[#0a1628] placeholder-[#8a9bb5] resize-none"
                ></textarea>
                <div className="text-xs text-[#8a9bb5] mt-1 text-right">
                  {formData.message.length}/500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-[#0a1628] text-white rounded-md text-sm uppercase tracking-widest font-bold hover:bg-[#0d1f3c] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer relative overflow-hidden group"
              >
                <span className="relative z-10">
                  {isSubmitting ? 'Sending...' : 'Schedule Consultation'}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#00e5c7] to-[#00b4d8] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <span className="absolute inset-0 flex items-center justify-center text-[#0a1628] font-bold text-sm uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity z-20 whitespace-nowrap">
                  {isSubmitting ? 'Sending...' : 'Schedule Consultation'}
                </span>
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-[#00e5c7]/10 border border-[#00e5c7]/30 rounded-md text-[#0a1628] text-sm">
                  Thank you! We’ll contact you shortly to schedule your
                  consultation.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  Something went wrong. Please try again or contact us directly.
                </div>
              )}

              <p className="text-xs text-[#8a9bb5] text-center">
                By submitting this form, you agree to our privacy policy and
                terms of service.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
